/**
 * Integrytas Group - Offline Data Sync (ODS)
 * Page: ODS.CONFLICTS
 *
 * Reconciliation inbox: open conflicts parked by the dispatcher (USER_RECONCILE
 * or FIELD_MERGE collisions). Shows base / client / server values and the
 * colliding fields; resolves via ods_resolve_conflict (CLIENT | SERVER | MERGED).
 */

import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid, GRID_TABLE_CLASS } from '../../components/personalization';
import type { GridColumnDef } from '../../components/personalization';
import { apiDb } from '../../lib/api_client';
import { hasAccess, type AuthSession } from '../../types/auth';
import { FacetFilter } from '../../components/common/FacetFilter';
import { ClearFiltersButton } from '../../components/common/ClearFiltersButton';
import { EmptyState } from '../../components/common/EmptyState';
import { ErrorBanner } from '../../components/common/ErrorBanner';
import { SkeletonRows } from '../../components/common/SkeletonRows';
import { RowActionsMenu } from '../../components/common/RowActionsMenu';
import { PageHeader } from '../../components/common/PageHeader';
import { ContentCard } from '../../components/common/ContentCard';
import { RefreshCw, Search } from 'lucide-react';

interface ConflictRow {
  ods_conflict_id: number; entity_name: string; record_id: string; strategy: string | null;
  base_values: Record<string, unknown> | null; client_values: Record<string, unknown> | null;
  server_values: Record<string, unknown> | null; conflicting_fields: string[]; created_at: string;
}

const CONFLICT_GRID_COLS: GridColumnDef[] = [
  { key: 'entity_name',        header: 'Entity',    defaultWidth: 200 },
  { key: 'record_id',         header: 'Record',    defaultWidth: 140 },
  { key: 'strategy',          header: 'Strategy',  defaultWidth: 160 },
  { key: 'conflicting_fields', header: 'Colliding', defaultWidth: 220 },
  { key: 'created_at',        header: 'Raised',    defaultWidth: 180 },
];

export interface OdsConflictsPageProps { session: AuthSession }

export function OdsConflictsPage({ session }: OdsConflictsPageProps) {
  return (
    <PersonalizationProvider pageCode="ODS.CONFLICTS" session={session}>
      <OdsConflictsPageInner session={session} />
    </PersonalizationProvider>
  );
}


const PAGE_CODE = 'ODS.CONFLICTS';
function OdsConflictsPageInner({ session }: OdsConflictsPageProps) {
  // §6/§17: the write below is page-scoped, so the affordance follows
  // csm_role_page_access. The server refuses it regardless; this stops the
  // page offering an action that can only error.
  const canChange = hasAccess(session, PAGE_CODE, 'change');
  const { t } = useTranslation();
  const [rows, setRows] = useState<ConflictRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [mergeText, setMergeText] = useState<Record<number, string>>({});
  const [filter, setFilter] = useState('');
  // §17.B5: per-column multi-value facets for the queue's logical columns
  // (entity + strategy). Status is fixed to OPEN here, so it is not faceted.
  const [facets, setFacets] = useState<{ entity: string[]; strategy: string[] }>({ entity: [], strategy: [] });
  const detail = useExpandedRows<number>();

  // §17.B13 related-children (expand/contract). A conflict's base/client/server
  // values are already shown inline in the Resolve column; there is no separate
  // child collection with resolved names to surface, so the panel is inert here.
  // TODO: wire relations.
  const buildRelatedGroups = (_id: number): RelatedChildGroup[] => [];
  const hasRelated = (id: number) => buildRelatedGroups(id).some(g => g.items.length > 0);

  const reload = async () => {
    setBusy(true); setError(null);
    try {
      const { data, error: e } = await apiDb.from('ods_conflict')
        .select('ods_conflict_id, entity_name, record_id, strategy, base_values, client_values, server_values, conflicting_fields, created_at')
        .eq('status', 'OPEN').order('created_at', { ascending: false });
      if (e) throw e;
      setRows((data ?? []) as ConflictRow[]);
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };
  useEffect(() => { void reload(); }, []);

  const resolve = async (id: number, resolution: 'CLIENT' | 'SERVER' | 'MERGED') => {
    setBusy(true); setError(null); setNotice(null);
    try {
      let values: unknown = null;
      if (resolution === 'MERGED') {
        try { values = JSON.parse(mergeText[id] ?? '{}'); } catch { throw new Error(t('ods_conflicts.err_merged_json', 'Merged values must be valid JSON.')); }
      }
      const { data, error: e } = await apiDb.rpc('ods_resolve_conflict', { p_conflict_id: id, p_resolution: resolution, p_values: values });
      if (e) throw e;
      const res = data as { ok: boolean; error_code?: string };
      if (!res?.ok) throw new Error(res?.error_code ?? 'ODS.RESOLVE_FAILED');
      setNotice(t('ods_conflicts.notice_resolved', 'Conflict {{id}} resolved ({{resolution}}).', { id, resolution }));
      await reload();
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  const fieldRow = (c: ConflictRow, f: string) => (
    <tr key={f} className={c.conflicting_fields.includes(f) ? 'bg-csm-danger-soft' : ''}>
      <td className="border-b border-csm-border-soft px-2 py-0.5 font-mono text-csm-2xs">{f}</td>
      <td className="border-b border-csm-border-soft px-2 py-0.5 text-csm-2xs">{JSON.stringify(c.base_values?.[f])}</td>
      <td className="border-b border-csm-border-soft px-2 py-0.5 text-csm-2xs">{JSON.stringify(c.client_values?.[f])}</td>
      <td className="border-b border-csm-border-soft px-2 py-0.5 text-csm-2xs">{JSON.stringify(c.server_values?.[f])}</td>
    </tr>
  );

  // Distinct facet options derived from the loaded rows (§17.B5).
  const facetOptions = useMemo(() => {
    const uniq = (vals: string[]) => [...new Set(vals.filter(Boolean))].sort();
    return {
      entity:   uniq(rows.map(c => c.entity_name)).map(v => ({ value: v, label: v })),
      strategy: uniq(rows.map(c => c.strategy ?? '')).map(v => ({ value: v, label: v })),
    };
  }, [rows]);

  const facetsActive = facets.entity.length + facets.strategy.length > 0;
  const anyFilterActive = facetsActive || filter.trim().length > 0;
  const clearAll = () => { setFilter(''); setFacets({ entity: [], strategy: [] }); };

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    // A row passes a facet when no values are selected, or it matches one (OR within
    // a facet, AND across facets).
    const matchesFacets = (c: ConflictRow) =>
      (facets.entity.length   === 0 || facets.entity.includes(c.entity_name)) &&
      (facets.strategy.length === 0 || facets.strategy.includes(c.strategy ?? ''));
    return rows
      .filter(c => !q ||
        c.entity_name.toLowerCase().includes(q) ||
        c.record_id.toLowerCase().includes(q) ||
        (c.strategy ?? '').toLowerCase().includes(q) ||
        (c.conflicting_fields ?? []).some(f => f.toLowerCase().includes(q)))
      .filter(matchesFacets);
  }, [rows, filter, facets]);

  return (
    <div className="flex h-full flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-[18px] overflow-auto p-6">
        <PageHeader
          title={t('ods_conflicts.heading', 'Sync Conflicts')}
          description={t('ods_conflicts.list.description', 'Offline-sync collisions parked for reconciliation — compare base, client and server values and resolve.')}
          actions={
            <button type="button" onClick={() => void reload()} className="btn-secondary flex items-center gap-1.5">
              <RefreshCw className="h-3.5 w-3.5" /> {t('ods_conflicts.refresh', 'Refresh')}
            </button>
          }
        />
        {error && (
          <ErrorBanner
            message={t('ods_conflicts.load_failed', "Couldn't complete the operation.")}
            detail={error}
            retry_label={t('common.retry', 'Retry')}
            on_retry={() => void reload()}
            details_label={t('common.details', 'Details')}
          />
        )}
        <ContentCard className="flex min-h-0 flex-1 flex-col">
          <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-csm-line bg-white px-3 py-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-csm-ink4" aria-hidden />
              <input
                type="text"
                value={filter}
                onChange={e => setFilter(e.target.value)}
                placeholder={t('ods_conflicts.filter_placeholder', 'Filter conflicts…')}
                className="h-8 w-60 rounded-csm-control border border-csm-border pl-8 pr-2 text-csm-md focus:border-primary focus:outline-none"
              />
            </div>
            <FacetFilter label={t('ods_conflicts.col.entity_name', 'Entity')} options={facetOptions.entity} selected={facets.entity} onChange={v => setFacets(f => ({ ...f, entity: v }))} />
            <FacetFilter label={t('ods_conflicts.col.strategy', 'Strategy')} options={facetOptions.strategy} selected={facets.strategy} onChange={v => setFacets(f => ({ ...f, strategy: v }))} />
            <ClearFiltersButton active={anyFilterActive} onClear={clearAll} label={t('common.clear_filters', 'Clear filters')} />
            <div className="ml-auto flex items-center gap-2">
              {busy && <span className="text-csm-xs text-csm-muted">{t('ods_conflicts.working', 'Working…')}</span>}
              {notice && <span className="text-csm-xs text-primary">{notice}</span>}
              <PersonalizationToggle />
            </div>
          </div>
          <div className="min-h-0 flex-1 overflow-auto">
            {busy && rows.length === 0 ? <SkeletonRows rows={8} /> : (
        <PersonalizedGrid componentKey="grid" columns={CONFLICT_GRID_COLS}>
          {(visibleCols, widths) => (
            <table className={GRID_TABLE_CLASS}>
              <thead className="bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
                <tr>
                  <th className="w-7 border-b border-csm-border px-2 py-1" title={t('ods_conflicts.col.related', 'Related records')} />
                  {visibleCols.map(col => (
                    <th key={col.key} className="border-b border-csm-border px-2 py-1 text-left"
                        style={widths[col.key] ? { width: widths[col.key] } : undefined}>{t('ods_conflicts.col.' + col.key, col.header)}</th>
                  ))}
                  <th className="border-b border-csm-border px-2 py-1 text-left" style={{ width: '1%' }}>{t('ods_conflicts.col_resolve', 'Resolve')}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(c => {
                  const fields = Array.from(new Set([...Object.keys(c.client_values ?? {}), ...(c.conflicting_fields ?? [])]));
                  return (
                    <Fragment key={c.ods_conflict_id}>
                    <tr className="align-top hover:bg-csm-surface-alt">
                      <td className="border-b border-csm-border-soft px-2 py-1 text-center align-middle">
                        <RelatedExpandButton open={detail.isOpen(c.ods_conflict_id)} hasChildren={hasRelated(c.ods_conflict_id)} onToggle={() => detail.toggle(c.ods_conflict_id)} expandLabel={t('ods_conflicts.related.expand', 'Show related records')} collapseLabel={t('ods_conflicts.related.collapse', 'Hide related')} tooltip={t('ods_conflicts.related.tooltip', 'Show related records')} />
                      </td>
                      {visibleCols.map(col => (
                        <td key={col.key} className="border-b border-csm-border-soft px-2 py-1 text-csm-sm"
                            style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                          {col.key === 'entity_name' && <span className="font-mono font-semibold">{c.entity_name}</span>}
                          {col.key === 'record_id'   && <span className="font-mono text-csm-xs">{c.record_id}</span>}
                          {col.key === 'strategy'    && <span className="text-csm-2xs text-csm-watermark">{c.strategy}</span>}
                          {col.key === 'conflicting_fields' && (
                            c.conflicting_fields.length > 0
                              ? <span className="rounded bg-csm-danger-soft px-1.5 py-0.5 font-mono text-csm-2xs text-csm-danger">{c.conflicting_fields.join(', ')}</span>
                              : <span className="text-csm-2xs text-csm-watermark">—</span>
                          )}
                          {col.key === 'created_at'  && <span className="text-csm-2xs text-csm-watermark">{new Date(c.created_at).toLocaleString()}</span>}
                        </td>
                      ))}
                      <td className="border-b border-csm-border-soft px-2 py-1">
                        <table className="mb-2 min-w-full border-collapse">
                          <thead><tr className="text-csm-2xs uppercase text-csm-ink-soft"><th className="px-2 text-left">{t('ods_conflicts.fld_field', 'Field')}</th><th className="px-2 text-left">{t('ods_conflicts.fld_base', 'Base')}</th><th className="px-2 text-left">{t('ods_conflicts.fld_client', 'Client')}</th><th className="px-2 text-left">{t('ods_conflicts.fld_server', 'Server')}</th></tr></thead>
                          <tbody>{fields.map(f => fieldRow(c, f))}</tbody>
                        </table>
                        <div className="mb-2">
                          <input value={mergeText[c.ods_conflict_id] ?? ''} onChange={e => setMergeText(m => ({ ...m, [c.ods_conflict_id]: e.target.value }))}
                                 placeholder={t('ods_conflicts.merge_placeholder', 'Merged values JSON (for "Apply Merged") e.g. {"field":"value"}')} className="h-7 w-full rounded border border-csm-border px-2 font-mono text-csm-2xs" />
                        </div>
                        <div className="flex items-center gap-2">
                          {canChange && (<button onClick={() => void resolve(c.ods_conflict_id, 'MERGED')} className="rounded border border-primary bg-primary px-2 py-0.5 text-csm-xs text-white hover:bg-primary-hover">{t('ods_conflicts.apply_merged', 'Apply Merged')}</button>)}
                          <RowActionsMenu
                            label={t('common.row_actions', 'Row actions')}
                            actions={[
                              { key: 'client', label: t('ods_conflicts.keep_client', 'Keep Client'), onSelect: () => void resolve(c.ods_conflict_id, 'CLIENT') },
                              { key: 'server', label: t('ods_conflicts.keep_server', 'Keep Server'), onSelect: () => void resolve(c.ods_conflict_id, 'SERVER') },
                            ]}
                          />
                        </div>
                      </td>
                    </tr>
                    {detail.isOpen(c.ods_conflict_id) && (
                      <tr>
                        <td colSpan={visibleCols.length + 2} className="border-b border-csm-border bg-csm-surface-alt/40 px-3 py-3">
                          <RelatedChildrenPanel groups={buildRelatedGroups(c.ods_conflict_id)} session={session} />
                        </td>
                      </tr>
                    )}
                    </Fragment>
                  );
                })}
                {filtered.length === 0 && !busy && (
                  <tr><td colSpan={visibleCols.length + 2} className="px-2 py-3">
                    <EmptyState variant={anyFilterActive ? 'no-match' : 'all-clear'} title={t('ods_conflicts.empty', 'No open conflicts.')} />
                  </td></tr>
                )}
              </tbody>
            </table>
          )}
            </PersonalizedGrid>
            )}
          </div>
        </ContentCard>
      </div>
    </div>
  );
}
